/**
 * Item Detail, second pass — Lab page.
 *
 * A working version of the redesign mockup of 2026-10-14
 * (claude.ai/artifact/RMCbiRhCqm1CVfeA7LaqHE), built on the product's Square/Mono
 * primitives and ds controls. It runs on the SAMPLE item in ./sampleItem.ts and
 * writes nothing: every interaction changes this page's own state only.
 *
 * It is a Lab preview of a possible new item page, not a second item page. The
 * live item page stays /prioritization/items/:id; if this design is adopted it
 * replaces that page there (build contract §2.6 applies at that point).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, Field, Select, TextInput } from '@/components/ds';
import {
  LABEL_MONO,
  btnPrimary,
  btnSecondary,
  btnDanger,
  chip,
  panel,
  panelInset,
  status,
  type Tone,
} from '@/components/mono/primitives';
import { CellBadge } from '@/components/prioritization/item-view/CellBadge';
import {
  ALTITUDE_NAMES,
  CHANGES_FROM_FIRST_PASS,
  ITEM_CATEGORIES,
  READINESS_STEPS,
  SAMPLE,
  type ItemState,
  type SampleCategory,
  type SessionResult,
} from './sampleItem';

// CellBadge now lives with the product's item view; kept exported here so this
// Lab file's old import path still works (Proposal 09 § 4, coupling 1).
export { CellBadge };

const MAX_ADDITIONAL = 5;

const STATE_LABEL: Record<ItemState, { text: string; tone: Tone }> = {
  triage: { text: 'In triage', tone: 'slate' },
  active: { text: 'Active', tone: 'green' },
  deferred: { text: 'Deferred', tone: 'mustard' },
  archived: { text: 'Archived', tone: 'coral' },
};

const TRIAGE_CHOICES: { key: ItemState | 'needs-info'; title: string; sets: string; tone: Tone }[] = [
  { key: 'active', title: 'Pursue', sets: 'Becomes active', tone: 'green' },
  { key: 'deferred', title: 'Defer', sets: 'Becomes deferred, hidden from the list', tone: 'mustard' },
  { key: 'archived', title: 'Reject', sets: 'Becomes archived, hidden from the list', tone: 'coral' },
  { key: 'needs-info', title: 'Needs info', sets: 'Stays in triage, asks a question', tone: 'slate' },
];

const RULE = { borderColor: 'var(--rule)' } as const;

/** Gold mark for what is new or moved since the first pass. */
function NewMark({ show, children }: { show: boolean; children: string }) {
  if (!show) return null;
  return (
    <span
      className="ml-2 whitespace-nowrap border px-1.5 align-middle font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--mustard)]"
      style={{ borderColor: 'var(--mustard)' }}
    >
      {children}
    </span>
  );
}

function ItemPageSectionRule({ title, hint, children }: { title: string; hint?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b pb-2" style={RULE}>
      <h3 className="text-[17px] font-semibold" style={{ color: 'var(--text-pri)' }}>
        {title}
      </h3>
      {hint && <span className="text-[13.5px] text-[var(--text-dim)]">{hint}</span>}
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

function GapLine({
  mark,
  tone,
  label,
  children,
  last = false,
}: {
  mark: string;
  tone: Tone;
  label: React.ReactNode;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-1.5 py-3 md:flex-nowrap ${last ? '' : 'border-b'}`} style={RULE}>
      <span {...status(tone, 'w-[104px] shrink-0')}>{mark}</span>
      <div className="w-full shrink-0 font-semibold text-[var(--text-pri)] md:w-[150px]">{label}</div>
      <div className="min-w-0 flex-1 text-[var(--text-sec)]">{children}</div>
    </div>
  );
}

function LinkActions({ actions }: { actions: string[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-3">
      {actions.map((a) => (
        <span key={a} className="cursor-pointer font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]">
          {a}
        </span>
      ))}
    </div>
  );
}

function CategoryCard({ c, lead }: { c: SampleCategory; lead?: boolean }) {
  const isQuestion = c.wording.trim().endsWith('?');
  return (
    <div className="min-w-0 border bg-[var(--bg-raised)] px-3 py-2.5" style={{ borderColor: lead ? 'var(--slate)' : 'var(--rule)' }}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={LABEL_MONO} style={lead ? { color: 'var(--text-sec)' } : undefined}>
          {c.category} · {lead ? 'primary' : 'additional'}
        </span>
        <span className={LABEL_MONO}>Alt {c.altitude}</span>
      </div>
      <div className="mt-1.5 text-[14.5px] text-[var(--text-pri)]">{c.wording}</div>
      <div className="mt-1.5 text-[13px] text-[var(--text-dim)]">
        {lead ? c.note : `${isQuestion ? 'Question' : 'Statement'}. ${c.note}`}
      </div>
    </div>
  );
}

function RankedAsBlock({ r }: { r: SessionResult }) {
  const a = r.rankedAs;
  const changed = a.changedSince.length > 0;
  return (
    <div className="mt-3 border-t pt-3" style={RULE}>
      <div className={LABEL_MONO}>What this group ranked</div>
      <div {...panel('mt-2 px-3 py-2.5')}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-semibold text-[var(--text-pri)]">{a.title}</span>
          <span className={LABEL_MONO}>
            {a.category} × altitude {a.altitude}
          </span>
        </div>
        <div className="mt-1 text-[14.5px] text-[var(--text-sec)]">{a.wording}</div>
        <div className="mt-1 font-mono text-[12.5px] text-[var(--text-ter)]">{a.values}</div>
      </div>
      <div className="mt-2 text-[13.5px] text-[var(--text-dim)]">
        {changed ? (
          <>
            <span {...status('mustard')}>Changed since</span> {a.changedSince.join(' · ')}. This group still sees the version above.{' '}
            <span className="cursor-pointer font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]">
              Compare with today
            </span>
          </>
        ) : (
          <>
            <span {...status('green')}>Unchanged</span> The item still reads this way.
          </>
        )}
      </div>
    </div>
  );
}

export function ItemDetailSecondPassLab() {
  const [view, setView] = useState<'before' | 'after'>('before');
  const [showNew, setShowNew] = useState(true);
  const [state, setState] = useState<ItemState>('active');
  const [triagePick, setTriagePick] = useState<string>('active');
  const [additional, setAdditional] = useState<SampleCategory[]>(SAMPLE.additional);
  const [adding, setAdding] = useState(false);
  const [draftCategory, setDraftCategory] = useState('Problem');
  const [draftAltitude, setDraftAltitude] = useState('3');
  const [draftWording, setDraftWording] = useState('');
  const [draftError, setDraftError] = useState('');
  const [contribution, setContribution] = useState(6);

  const usedCategories = useMemo(
    () => new Set([SAMPLE.primary.category, ...additional.map((c) => c.category)]),
    [additional]
  );
  const openCategories = ITEM_CATEGORIES.filter((c) => !usedCategories.has(c));

  const pickTriage = (key: string) => {
    setTriagePick(key);
    setState(key === 'needs-info' ? 'triage' : (key as ItemState));
  };

  const startAdding = () => {
    setDraftCategory(openCategories[0] ?? 'Problem');
    setDraftAltitude('3');
    setDraftWording('');
    setDraftError('');
    setAdding(true);
  };

  const saveCategory = () => {
    const wording = draftWording.trim();
    if (!wording) {
      setDraftError('Say what the item is in this category, as a statement or a question.');
      return;
    }
    setAdditional((prev) => [
      ...prev,
      {
        id: `new-${prev.length}`,
        category: draftCategory,
        altitude: Number(draftAltitude) as SampleCategory['altitude'],
        wording,
        note: 'Added just now on this page.',
      },
    ]);
    setAdding(false);
  };

  const st = STATE_LABEL[state];
  const inList = state === 'active' || state === 'triage';

  return (
    <div className="mx-auto max-w-[1260px] pb-24">
      {/* Lab framing */}
      <header className="border-b pb-5 pt-2" style={RULE}>
        <div className={LABEL_MONO}>Lab · item detail, second pass · sample item · writes nothing</div>
        <h1 className="mt-2 font-serif text-[30px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
          Item detail, with every item ruling from 13 October
        </h1>
        <p className="mt-2 max-w-[78ch] text-[var(--text-sec)]">
          A working version of the second-pass mockup. Everything on it is sample content and nothing you do here is saved. Gold marks
          show what is new or moved since the September design.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-2" role="group" aria-label="Which state to show">
            <button {...(view === 'before' ? btnPrimary() : btnSecondary())} aria-pressed={view === 'before'} onClick={() => setView('before')}>
              Before ranking
            </button>
            <button {...(view === 'after' ? btnPrimary() : btnSecondary())} aria-pressed={view === 'after'} onClick={() => setView('after')}>
              After ranking
            </button>
          </div>
          <Checkbox label="Show what changed" checked={showNew} onChange={(e) => setShowNew(e.target.checked)} />
          <Link to="/prioritization/items" className="ml-auto font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]">
            Today’s item page →
          </Link>
        </div>
      </header>

      <div {...panel('mt-6 px-4 py-5 md:px-7')}>
        <div className={LABEL_MONO}>‹ Back to {view === 'before' ? 'items' : 'Q3 Engineering'}</div>

        {/* Header: every action on one row above the title */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <CellBadge category={SAMPLE.primary.category} altitude={SAMPLE.primary.altitude} />
            <span {...status(st.tone)}>● {st.text}</span>
            {view === 'after' && (
              <>
                <span {...chip('mustard')}>Priority · Q3 FY26 · #3</span>
                <span {...chip('green')}>In the Q3 plan</span>
              </>
            )}
            <NewMark show={showNew}>header reworked</NewMark>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button {...btnSecondary()}>Print view</button>
            <button {...btnSecondary()}>Integrations · 1</button>
            <button {...btnSecondary()}>Watch</button>
            <button {...btnSecondary()}>Copy link</button>
            {view === 'before' ? (
              <>
                <button {...btnDanger()}>Retire</button>
                <button {...btnPrimary()}>Add to an item pool</button>
              </>
            ) : (
              <>
                <button {...btnSecondary()}>Raise a concern</button>
                <button {...btnPrimary()}>Open the commitment</button>
              </>
            )}
          </div>
        </div>

        <h2 className="mt-4 text-[26px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
          {SAMPLE.title}
        </h2>
        <p className="mt-2 max-w-[84ch] text-[var(--text-sec)]">{SAMPLE.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[13.5px] text-[var(--text-dim)]">
          <span>
            Owner <strong className="font-semibold text-[var(--text-pri)]">{SAMPLE.owner}</strong>
          </span>
          {view === 'before' ? (
            <>
              <span>Created {SAMPLE.created}</span>
              <span>Updated {SAMPLE.updated}</span>
              {SAMPLE.tags.map((t) => (
                <span key={t} className="bg-[var(--surface)] px-2 py-0.5 text-[13px] text-[var(--text-ter)]">
                  {t}
                </span>
              ))}
            </>
          ) : (
            <>
              <span>Ranked 25 Oct</span>
              <span>Published 28 Oct</span>
            </>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-10 border-t pt-5 lg:flex-row" style={RULE}>
          <div className="min-w-0 flex-1 space-y-9">
            {view === 'before' ? (
              <>
                {/* The one rule every item answers the same way */}
                <section>
                  <ItemPageSectionRule
                    title="Can it become a priority?"
                    hint={
                      <>
                        the one check every item answers the same way<NewMark show={showNew}>new</NewMark>
                      </>
                    }
                  />
                  <div className="mt-3 flex gap-4 border px-4 py-3.5" style={{ borderColor: 'var(--green)', background: 'var(--green-dim)' }}>
                    <span {...status('green', 'pt-0.5')}>✓ Yes</span>
                    <div className="min-w-0 text-[var(--text-sec)]">
                      <strong className="text-[var(--text-pri)]">
                        It has values on {SAMPLE.criteriaWithValues.length} criteria:
                      </strong>{' '}
                      {SAMPLE.criteriaWithValues.map((c) => `${c.name} ${c.value}`).join(' and ')}. An item becomes a potential priority once
                      at least one of its attributes is used as a criterion and has a value. Whether those are the right criteria depends on
                      where it is judged, below.
                      <div className="mt-1 text-[13.5px] text-[var(--text-dim)]">Your rule from 13 October, held for filing. It is not in the canon yet.</div>
                    </div>
                  </div>
                </section>

                <section>
                  <ItemPageSectionRule title="Is it well formed?" hint="true no matter where it’s judged" />

                  <GapLine mark={`● ${st.text}`} tone={st.tone} label={<>Triage<NewMark show={showNew}>states named</NewMark></>}>
                    <strong className="text-[var(--text-pri)]">{TRIAGE_CHOICES.find((c) => c.key === triagePick)?.title}</strong>
                    {inList ? ', so it shows in the item list.' : ', so it no longer shows in the item list.'}
                    <div className="my-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                      {TRIAGE_CHOICES.map((c) => {
                        const on = triagePick === c.key;
                        return (
                          <button
                            key={c.key}
                            {...btnSecondary('!h-auto !flex-col !items-start !justify-start !whitespace-normal py-2.5 text-left normal-case')}
                            style={{
                              borderColor: on ? 'var(--green)' : 'var(--rule-bright)',
                              background: on ? 'var(--green-dim)' : undefined,
                            }}
                            aria-pressed={on}
                            onClick={() => pickTriage(c.key)}
                          >
                            <span {...status(c.tone)}>{c.title}</span>
                            <span className="mt-1 font-sans text-[13px] normal-case tracking-normal text-[var(--text-dim)]">{c.sets}</span>
                          </button>
                        );
                      })}
                    </div>
                  </GapLine>

                  {SAMPLE.wellFormed.slice(0, 1).map((r) => (
                    <GapLine key={r.label} mark={r.mark} tone={r.tone} label={r.label}>
                      {r.body}
                    </GapLine>
                  ))}

                  <GapLine mark="! Proposal" tone="mustard" label={<>Categories<NewMark show={showNew}>moved here</NewMark></>}>
                    One primary category and {additional.length} additional. They are all this one item; each says it in its own words, as a
                    statement or a question. Additional categories can put the item in more tracks.
                    <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      <CategoryCard c={SAMPLE.primary} lead />
                      {additional.map((c) => (
                        <CategoryCard key={c.id} c={c} />
                      ))}
                      {!adding && additional.length < MAX_ADDITIONAL && openCategories.length > 0 && (
                        <button
                          {...btnSecondary('!h-auto min-h-[96px] border-dashed text-[var(--text-ter)]')}
                          onClick={startAdding}
                        >
                          + Add an additional category
                        </button>
                      )}
                    </div>
                    {adding && (
                      <div {...panelInset('mt-3 space-y-3 p-3')}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Category" htmlFor="idsp-cat">
                            <Select
                              id="idsp-cat"
                              value={draftCategory}
                              onChange={(e) => setDraftCategory(e.target.value)}
                              options={openCategories.map((c) => ({ value: c, label: c }))}
                            />
                          </Field>
                          <Field label="Altitude" htmlFor="idsp-alt">
                            <Select
                              id="idsp-alt"
                              value={draftAltitude}
                              onChange={(e) => setDraftAltitude(e.target.value)}
                              options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} · ${ALTITUDE_NAMES[n]}` }))}
                            />
                          </Field>
                        </div>
                        <Field
                          label="What it is in this category"
                          htmlFor="idsp-wording"
                          hint="A statement or a question. End with a question mark to make it a question."
                          error={draftError || undefined}
                        >
                          <TextInput
                            id="idsp-wording"
                            value={draftWording}
                            invalid={!!draftError}
                            onChange={(e) => {
                              setDraftWording(e.target.value);
                              if (draftError) setDraftError('');
                            }}
                            placeholder="Could this…? / This item…"
                          />
                        </Field>
                        <div className="flex gap-2">
                          <button {...btnPrimary()} onClick={saveCategory}>
                            Add category
                          </button>
                          <button {...btnSecondary()} onClick={() => setAdding(false)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    <LinkActions actions={['Review the altitude proposal', 'Set which one leads', 'Suggest categories']} />
                    <div className="mt-1 text-[13.5px] text-[var(--text-dim)]">
                      Altitude runs 1 to 5. Altitude {SAMPLE.primary.altitude} is {ALTITUDE_NAMES[SAMPLE.primary.altitude]}. It decides which tracks
                      the item can enter.
                    </div>
                  </GapLine>

                  {SAMPLE.wellFormed.slice(1).map((r, i, arr) => (
                    <GapLine key={r.label} mark={r.mark} tone={r.tone} label={r.label} last={i === arr.length - 1}>
                      {r.body}
                      {r.actions && <LinkActions actions={r.actions} />}
                    </GapLine>
                  ))}
                </section>

                <section>
                  <ItemPageSectionRule title="Where it’s being judged" hint="2 cycles · 3 item pools · each answers for itself" />
                  <p className="mt-2.5 text-[13.5px] text-[var(--text-dim)]">
                    One item, many judgments at once. Each block is one item pool inside a track. A session’s criteria decide the answer, so two
                    sessions can disagree and both be right.
                  </p>
                  {SAMPLE.pools.map((p) => (
                    <div key={p.pool} className="mt-5">
                      <div className={LABEL_MONO} style={{ color: 'var(--text-pri)' }}>
                        {p.cycle === 'Not in a cycle' ? p.cycle : `Cycle · ${p.cycle}`}
                      </div>
                      <div {...panelInset('mt-2 px-4 py-3.5')}>
                        <div className="flex flex-wrap items-baseline justify-between gap-3">
                          <div className="min-w-0">
                            <span className="font-semibold text-[var(--text-pri)]">{p.pool}</span> <span className={LABEL_MONO}>item pool</span>
                            <div className="mt-0.5 text-[13.5px] text-[var(--text-dim)]">
                              {p.where}
                              {p.step === null && <NewMark show={showNew}>pool kept separate</NewMark>}
                            </div>
                          </div>
                          <span {...status(p.verdictTone)}>{p.verdict}</span>
                        </div>
                        {p.step !== null && (
                          <div className="flex flex-wrap items-center gap-2 pb-2 pt-3">
                            {READINESS_STEPS.map((s, i) => (
                              <span key={s} className="flex items-center gap-2">
                                {i > 0 && <span className="h-px w-4 bg-[var(--rule-bright)]" />}
                                <span
                                  className="font-mono text-[12px] uppercase tracking-[0.06em]"
                                  style={{
                                    color: i < p.step! ? 'var(--green-text)' : i === p.step ? 'var(--mustard)' : 'var(--text-dim)',
                                    boxShadow: i === p.step ? 'inset 0 -2px 0 0 var(--mustard)' : undefined,
                                  }}
                                >
                                  {s}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="mt-1 border-t" style={RULE}>
                          {p.checks.map((c, i) => (
                            <GapLine key={c.label} mark={c.mark} tone={c.tone} label={c.label} last={i === p.checks.length - 1 && !p.sessions}>
                              {c.body}
                            </GapLine>
                          ))}
                        </div>
                        {p.sessions && (
                          <div className="pt-2">
                            <div className={LABEL_MONO}>Sessions on this pool</div>
                            {p.sessions.map((s) => (
                              <div key={s.name} className="flex flex-wrap items-center gap-3 py-2">
                                <span className={`${LABEL_MONO} w-[70px]`}>{s.date}</span>
                                <span className="min-w-0 flex-1 text-[var(--text-sec)]">{s.name}</span>
                                <span {...status(s.tone)}>{s.verdict}</span>
                              </div>
                            ))}
                            <div className="text-[13.5px] text-[var(--text-dim)]">
                              Both rank it as the Commitment wording. When they do, that wording is kept with their result.
                              <NewMark show={showNew}>new</NewMark>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </section>

                <section>
                  <ItemPageSectionRule title="What we know">
                    <button {...btnSecondary()}>Add evidence</button>
                    <button {...btnSecondary()}>Log an impact event</button>
                  </ItemPageSectionRule>
                  <div className="grid grid-cols-2 border-b md:grid-cols-4" style={RULE}>
                    {SAMPLE.values.map((v, i) => (
                      <div key={v.name} className={`min-w-0 py-3.5 ${i > 0 ? 'md:border-l md:pl-4' : ''}`} style={RULE}>
                        <div className={LABEL_MONO}>{v.name}</div>
                        <div className="mt-1.5 text-[24px] font-semibold tabular-nums" style={{ color: v.value ? 'var(--text-pri)' : 'var(--text-dim)' }}>
                          {v.value ?? '—'}
                        </div>
                        <span {...status(v.tone, 'mt-1')}>{v.note}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-8">
                    <div className="min-w-0 flex-1">
                      <div className={LABEL_MONO}>Official value · Reach</div>
                      <div className="mt-2 font-mono text-[var(--text-pri)]">8 of 10</div>
                      <div className="mt-1 text-[13.5px] text-[var(--text-dim)]">Accepted by Ray Delgado, 6 Sep. A different value raises a change request.</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={LABEL_MONO}>Your contribution · Reach</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {[2, 4, 6, 8, 10].map((n) => (
                          <button
                            key={n}
                            {...btnSecondary('!h-8 !px-3')}
                            style={{
                              borderColor: contribution === n ? 'var(--slate)' : 'var(--rule-bright)',
                              background: contribution === n ? 'var(--slate-dim)' : undefined,
                            }}
                            aria-pressed={contribution === n}
                            onClick={() => setContribution(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <div className="mt-1 text-[13.5px] text-[var(--text-dim)]">Contributed, not official. Add a reason so the room can read it.</div>
                    </div>
                  </div>
                </section>

                <section>
                  <ItemPageSectionRule title="Relationships">
                    <button {...btnSecondary()}>Find links</button>
                    <button {...btnSecondary()}>Types guide</button>
                  </ItemPageSectionRule>
                  <p className="mt-2.5 text-[13.5px] text-[var(--text-dim)]">
                    Links to other items. Categories are no longer here: one item under several categories is still one item.
                    <NewMark show={showNew}>moved out</NewMark>
                  </p>
                  {(['side', 'updown'] as const).map((g) => (
                    <div key={g}>
                      <div className={`${LABEL_MONO} pb-1 pt-4`}>{g === 'side' ? 'Side by side · peers' : 'Up and down · bigger and smaller work'}</div>
                      {SAMPLE.relationships
                        .filter((r) => r.group === g)
                        .map((r) => (
                          <div key={r.kind} className="flex flex-wrap items-center gap-3 border-b py-2.5" style={RULE}>
                            <span className={`${LABEL_MONO} w-[128px]`}>{r.kind}</span>
                            <span className="min-w-0 flex-1 text-[var(--text-sec)]">
                              {r.text}
                              {r.strength && (
                                <span className="ml-2 border px-1.5 font-mono text-[11px] uppercase text-[var(--text-dim)]" style={RULE}>
                                  {r.strength}
                                </span>
                              )}
                            </span>
                            <span {...status(r.tone)}>{r.verdict}</span>
                          </div>
                        ))}
                    </div>
                  ))}
                </section>

                <section>
                  <ItemPageSectionRule title="Where it came from">
                    <button {...btnSecondary()}>Split, merge or retire</button>
                    <button {...btnSecondary()}>Item journey</button>
                  </ItemPageSectionRule>
                  <div className="flex flex-wrap items-stretch gap-y-2 pt-3.5">
                    <div {...panelInset('min-w-[160px] px-3.5 py-2.5')}>
                      <div className={LABEL_MONO}>Merged · 2 Aug</div>
                      <div className="mt-1 text-sm text-[var(--text-pri)]">Valve supplier backup plan</div>
                    </div>
                    <span className="flex items-center px-2.5 font-mono text-[12px] text-[var(--text-dim)]">merge →</span>
                    <div {...panel('min-w-[160px] px-3.5 py-2.5')} style={{ borderColor: 'var(--slate)' }}>
                      <div className={LABEL_MONO}>This item</div>
                      <div className="mt-1 text-sm text-[var(--text-pri)]">Alternate Valve Supplier Qualification</div>
                    </div>
                    <span className="flex items-center px-2.5 font-mono text-[12px] text-[var(--text-dim)]">fork →</span>
                    <div {...panelInset('min-w-[160px] px-3.5 py-2.5')}>
                      <div className={LABEL_MONO}>Forked · 9 Sep</div>
                      <div className="mt-1 text-sm text-[var(--text-pri)]">Tooling transfer variant</div>
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section>
                  <ItemPageSectionRule
                    title="Is it a priority?"
                    hint={
                      <>
                        only a published priority list says so<NewMark show={showNew}>new</NewMark>
                      </>
                    }
                  />
                  <div className="mt-3 flex gap-4 border px-4 py-3.5" style={{ borderColor: 'var(--mustard)', background: 'var(--mustard-dim)' }}>
                    <span {...status('mustard', 'pt-0.5')}>Yes · #3</span>
                    <div className="min-w-0 text-[var(--text-sec)]">
                      <strong className="text-[var(--text-pri)]">Q3 FY26 priority list, published 28 Oct</strong> by Maya Chen. Third of 8 above
                      the cut line. This is the only list on this page that carries authority; the session results below are inputs to it.
                    </div>
                  </div>
                </section>

                <section>
                  <ItemPageSectionRule
                    title="How sessions ranked it"
                    hint={
                      <>
                        each group’s version is kept as they saw it<NewMark show={showNew}>new</NewMark>
                      </>
                    }
                  />
                  {SAMPLE.sessionResults.map((r) => (
                    <div key={r.name} {...panelInset('mt-3 px-4 py-3.5')}>
                      <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <span className="font-semibold text-[var(--text-pri)]">{r.name}</span>
                          <div className="mt-0.5 text-[13.5px] text-[var(--text-dim)]">{r.where}</div>
                        </div>
                        <span {...status(r.tone)}>{r.verdict}</span>
                      </div>
                      {r.change && (
                        <div className="mt-3 border-t pt-3 text-[var(--text-sec)]" style={RULE}>
                          <span {...status('mustard')}>Order changed after the session</span> {r.change.by} moved it from {r.change.from} to{' '}
                          {r.change.to}: “{r.change.reason}” Everyone in the session was told.
                          <div className="mt-1 text-[13.5px] text-[var(--text-dim)]">
                            The result stays {r.change.from}; the ranking that went forward is {r.change.to}. Whether a track allows this is roadmap
                            item S-33; today the permission alone decides.
                          </div>
                        </div>
                      )}
                      <RankedAsBlock r={r} />
                    </div>
                  ))}
                </section>

                <section>
                  <ItemPageSectionRule
                    title="In the plan"
                    hint={
                      <>
                        the plan holds a frozen copy of the priority list<NewMark show={showNew}>new</NewMark>
                      </>
                    }
                  />
                  <div className="flex flex-wrap items-center gap-3 border-b py-2.5" style={RULE}>
                    <span className={`${LABEL_MONO} w-[128px]`}>Plan of record</span>
                    <span className="min-w-0 flex-1 text-[var(--text-sec)]">Q3 FY26 plan, authorized 30 Oct</span>
                    <span {...status('green')}>In</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 border-b py-2.5" style={RULE}>
                    <span className={`${LABEL_MONO} w-[128px]`}>Sequence</span>
                    <span className="min-w-0 flex-1 text-[var(--text-sec)]">
                      Scheduled 5th, not 3rd: it waits on the BOM revision. The reason is recorded with the plan.
                    </span>
                    <span {...status('mustard')}>Differs from rank</span>
                  </div>
                </section>

                <section>
                  <ItemPageSectionRule title="How it turned out" hint="predicted against actual" />
                  <div className="grid grid-cols-2 border-b md:grid-cols-4" style={RULE}>
                    {[
                      { l: 'Predicted effort', v: '5.0' },
                      { l: 'Actual effort', v: '7.5', n: '50% over', t: 'mustard' as Tone },
                      { l: 'Reach, measured', v: null, n: 'Not yet in use', t: 'neutral' as Tone },
                      { l: 'Impact avoided', v: '$42k', n: '1 event', t: 'green' as Tone },
                    ].map((x, i) => (
                      <div key={x.l} className={`min-w-0 py-3.5 ${i > 0 ? 'md:border-l md:pl-4' : ''}`} style={RULE}>
                        <div className={LABEL_MONO}>{x.l}</div>
                        <div className="mt-1.5 text-[24px] font-semibold tabular-nums" style={{ color: x.v ? 'var(--text-pri)' : 'var(--text-dim)' }}>
                          {x.v ?? '—'}
                        </div>
                        {x.n && <span {...status(x.t!, 'mt-1')}>{x.n}</span>}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>

          {/* Properties rail */}
          <aside className="min-w-0 lg:w-[300px] lg:shrink-0">
            <div className={`${LABEL_MONO} border-b pb-2`} style={{ borderColor: 'var(--rule-bright)' }}>
              Properties
            </div>
            <div className={`${LABEL_MONO} mb-2 mt-5`}>What it is</div>
            <dl className="grid grid-cols-[118px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-[var(--text-dim)]">Category</dt>
              <dd className="text-[var(--text-sec)]">
                {SAMPLE.primary.category} · +{additional.length} more
              </dd>
              <dt className="text-[var(--text-dim)]">Altitude</dt>
              <dd className="text-[var(--text-sec)]">
                {SAMPLE.primary.altitude} · {ALTITUDE_NAMES[SAMPLE.primary.altitude]}
              </dd>
              <dt className="text-[var(--text-dim)]">Work type</dt>
              <dd className="text-[var(--text-sec)]">Qualification</dd>
              <dt className="text-[var(--text-dim)]">Launch type</dt>
              <dd className={view === 'before' ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'}>{view === 'before' ? 'Not set' : 'Staged'}</dd>
              <dt className="text-[var(--text-dim)]">Effort</dt>
              <dd className={view === 'before' ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'}>{view === 'before' ? 'Not set' : 'L'}</dd>
            </dl>

            <div className={`${LABEL_MONO} mb-2 mt-5`}>
              Lists it’s in<NewMark show={showNew}>new</NewMark>
            </div>
            <div className="border-t" style={RULE}>
              {[
                { n: 'Collections', c: 1, d: 'Ops risk review · a loose grouping, no weight' },
                { n: 'Item pools', c: 3, d: 'Groups to compare or run scenarios on' },
                { n: 'Session results', c: view === 'before' ? 0 : 3, d: view === 'before' ? 'Not ranked in any session yet' : 'Each kept as its group saw the item' },
                { n: 'Priority lists', c: view === 'before' ? 0 : 1, d: view === 'before' ? 'Not chosen as a priority yet' : 'Q3 FY26 · published · #3' },
                { n: 'Plans', c: view === 'before' ? 0 : 1, d: view === 'before' ? 'Not in a plan of record' : 'Q3 FY26 plan of record' },
              ].map((l) => (
                <div key={l.n} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 border-b py-2 text-sm" style={RULE}>
                  <span className="text-[var(--text-pri)]">{l.n}</span>
                  <span className="font-mono text-[12px] tabular-nums text-[var(--text-ter)]">{l.c}</span>
                  <span className="col-span-2 text-[13px] text-[var(--text-dim)]">{l.d}</span>
                </div>
              ))}
            </div>

            <div className={`${LABEL_MONO} mb-2 mt-5`}>
              Who holds it<NewMark show={showNew}>names fixed</NewMark>
            </div>
            <dl className="grid grid-cols-[118px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-[var(--text-dim)]">Owner</dt>
              <dd className="text-[var(--text-sec)]">{SAMPLE.owner}</dd>
              <dt className="text-[var(--text-dim)]">Item managers</dt>
              <dd className="text-[var(--text-sec)]">2 in the General queue</dd>
              <dt className="text-[var(--text-dim)]">Can see it</dt>
              <dd className="text-[var(--text-sec)]">All members</dd>
            </dl>

            <div className="mt-4 border-t pt-3 text-[13px] text-[var(--text-dim)]" style={RULE}>
              Sample item. Created 25 Jul by Ray Delgado from intake.
            </div>
          </aside>
        </div>
      </div>

      {/* What changed */}
      <div {...panel('mt-6 px-4 py-5 md:px-6')}>
        <h3 className="text-[17px] font-semibold" style={{ color: 'var(--text-pri)' }}>
          What changed from the 22 September version
        </h3>
        <div className="mt-3">
          {CHANGES_FROM_FIRST_PASS.map((c) => (
            <div key={c.ruling} className="grid grid-cols-1 gap-1 border-b py-2.5 text-sm md:grid-cols-[220px_minmax(0,1fr)] md:gap-4" style={RULE}>
              <span className="font-semibold text-[var(--text-pri)]">{c.ruling}</span>
              <span className="text-[var(--text-sec)]">{c.landed}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ItemDetailSecondPassLab;
